import {
  FiRefreshCw,
  FiCheckCircle,
  FiTag,
  FiList,
  FiHash,
  FiClock,
  FiSettings,
  FiChevronDown,
  FiLayers,
  FiSliders,
  FiMaximize2,
  FiTarget,
  FiToggleRight,
  FiChevronUp,
  FiPlus,
  FiPlay,
} from "react-icons/fi";
import { HiDuplicate } from "react-icons/hi";
import { PiGraphFill } from "react-icons/pi";
import {
  ProgressBar,
  formatTimeRemaining,
  formatElapsedTime,
  getCurrentTimeInUTC,
  formatDateTime,
} from "./utils";
import ReactFlow, { Background, ReactFlowProvider } from "reactflow";
import { nodeTypes } from "../Workflows/screens/DataDiscovery/Graph/GraphNodes";
import { processData } from "../Workflows/screens/DataDiscovery/GraphUtils";
import { updateGraphData } from "../Workflows/screens/DataDiscovery/AutoSuggest/AutoSuggestUtils";
import { ErrorDataTable } from "./ErrorDataTable";
import { useState, useContext } from "react";
import { TagSelectionModal } from "../Workflows/screens/DataDiscovery/AutoSuggest/TagSelectionModal";
import { GraphContext } from "../../../contexts/GraphContext";
import { tagService, metadataService } from "../../../services/api";
import toast from "react-hot-toast";
import { BaseContext } from "../../../contexts/BaseContext";
import { DATE_FORMAT_OPTIONS } from "../../../config/config";

const isTrackerStale = (tracker) => {
  return (
    tracker.status === "in_progress" &&
    Date.now() - new Date(tracker.start_time).getTime() >= 24 * 60 * 60 * 1000
  );
};

const isTrackerActive = (tracker) => {
  return (
    tracker.status === "in_progress" &&
    Date.now() - new Date(tracker.start_time).getTime() < 24 * 60 * 60 * 1000
  );
};

const renderTimelineInfo = (tracker) => {
  const startTime = new Date(tracker.start_time);
  const elapsedMs = getCurrentTimeInUTC() - startTime;
  const elapsedStr = formatElapsedTime(elapsedMs);
  const progress = isTrackerActive(tracker)
    ? tracker.completed_progress_increments / tracker.total_progress_increments
    : null;

  return (
    <div className="text-xs text-gray-500 text-center">
      {/* Only show elapsed time for active trackers */}
      {isTrackerActive(tracker) && <div>Time elapsed: {elapsedStr}</div>}

      {/* Show remaining time or calculation message */}
      {progress !== null && (
        <div>
          {progress > 0
            ? `~${formatTimeRemaining(elapsedMs, progress)} remaining`
            : "Calculating remaining time..."}
        </div>
      )}

      {/* Show tag creation count if available */}
      {tracker.tags_created !== undefined && (
        <div>
          {tracker.tags_created.toLocaleString()} new tag value pair
          {tracker.tags_created !== 1 && "s"}
        </div>
      )}
    </div>
  );
};

const renderProgressCell = (tracker) => {
  // If stale, show as failed instead
  if (isTrackerStale(tracker)) {
    return (
      <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500">
        <div className="flex items-center">
          <span className="text-red-600 mr-2">✗</span>
          <span className="text-gray-500">Failed (Timeout)</span>
        </div>
      </td>
    );
  }

  const isComplete = tracker.status === "completed";
  const progress =
    tracker.completed_progress_increments >= tracker.total_progress_increments
      ? 1
      : Math.min(
          0.99,
          Math.round(
            (tracker.completed_progress_increments /
              Math.max(tracker.total_progress_increments, 1)) *
              100,
          ) / 100,
        );

  if (isComplete && progress === 1) {
    return (
      <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500">
        <div className="flex items-center">
          <span className="text-green-600 mr-2">✓</span>
          <span className="text-gray-500">Complete</span>
        </div>
      </td>
    );
  }

  return (
    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 w-full">
      <ProgressBar value={progress} active={tracker.status === "in_progress"} />
    </td>
  );
};

const renderFilesCell = (tracker) => {
  if (
    tracker.task_type === "auto_create" ||
    tracker.task_type === "standardize"
  ) {
    return (
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left">
        N/A
      </td>
    );
  }
  return (
    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left">
      {Array.isArray(tracker.files) && tracker.files.length > 0
        ? `${tracker.files.length.toLocaleString()} files`
        : `${tracker.total_progress_increments.toLocaleString()} files`}
    </td>
  );
};

const ChunkMappingCell = ({ tracker }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const itemsPerPage = 6;

  const { deasyApiKey } = useContext(BaseContext);

  if (
    tracker.standard_mapping === null ||
    Object.keys(tracker.standard_mapping).length === 0
  ) {
    return null;
  }

  const mappingEntries = Object.entries(tracker.standard_mapping);
  const totalPages = Math.ceil(mappingEntries.length / itemsPerPage);
  const startIndex = currentPage * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentEntries = mappingEntries.slice(startIndex, endIndex);

  const totalMappings = mappingEntries.length;
  const totalMappedItems = mappingEntries.reduce(
    (sum, [_, values]) => sum + values.length,
    0,
  );

  const handleApplyStandardization = async () => {
    if (!deasyApiKey || !tracker.tag_name || !tracker.vdb_data_source) {
      toast.error("Missing required information to apply standardization");
      return;
    }

    setIsApplying(true);
    try {
      await metadataService.standardizeMetadataDB(
        tracker.tag_name,
        tracker.standard_mapping,
        deasyApiKey,
        tracker.vdb_data_source,
        tracker?.llm_vendor || null,
      );

      toast.success("Standardization applied successfully!");
    } catch (error) {
      console.error("Error applying standardization:", error);
      toast.error(
        `Failed to apply standardization: ${error.response?.data?.message || error.message || "Unknown error"}`,
      );
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <div className="w-full bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <FiLayers className="text-emerald-600" size={20} />
          <h3 className="text-xl font-semibold text-gray-800">Chunk Mapping</h3>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-sm text-gray-500 bg-gray-100 px-3 py-2 rounded-full">
            {totalMappings} categories • {totalMappedItems} items
          </div>

          <button
            onClick={handleApplyStandardization}
            disabled={isApplying}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
          >
            {isApplying ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                Applying...
              </>
            ) : (
              <>
                <FiPlay size={16} />
                Apply Standardization
              </>
            )}
          </button>

          {totalPages > 1 && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              title={isExpanded ? "Collapse" : "Expand"}
            >
              {isExpanded ? (
                <FiChevronUp className="text-gray-500" size={18} />
              ) : (
                <FiChevronDown className="text-gray-500" size={18} />
              )}
            </button>
          )}
        </div>
      </div>

      {/* Grid layout for better use of full width */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {currentEntries.map(([category, mappedValues], index) => (
          <div
            key={`${category}-${index}`}
            className="p-4 bg-gray-50 rounded-lg border border-gray-100 hover:border-emerald-200 transition-colors"
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-2 h-2 bg-emerald-600 rounded-full mt-2"></div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-gray-800 mb-2 break-words">
                  {category}
                </h4>
                <div className="flex items-center gap-2 mb-2">
                  <FiTarget className="text-gray-400 flex-shrink-0" size={12} />
                  <span className="text-xs font-medium text-gray-600">
                    Maps to {mappedValues.length} item
                    {mappedValues.length !== 1 ? "s" : ""}
                  </span>
                </div>

                {/* Show preview of mapped values */}
                <div className="space-y-1">
                  {mappedValues
                    .slice(0, isExpanded ? mappedValues.length : 2)
                    .map((value, valueIndex) => (
                      <div
                        key={valueIndex}
                        className="flex items-center gap-2 text-xs text-gray-600"
                      >
                        <div className="w-1 h-1 bg-gray-400 rounded-full flex-shrink-0"></div>
                        <span className="truncate">{value}</span>
                      </div>
                    ))}

                  {!isExpanded && mappedValues.length > 2 && (
                    <div className="flex items-center gap-2 text-xs text-gray-500 italic">
                      <div className="w-1 h-1 bg-gray-300 rounded-full flex-shrink-0"></div>
                      <span>+ {mappedValues.length - 2} more items</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
          <div className="text-sm text-gray-500">
            Page {currentPage + 1} of {totalPages}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
              disabled={currentPage === 0}
              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Previous page"
            >
              <svg
                className="w-4 h-4 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentPage(i)}
                  className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                    i === currentPage
                      ? "bg-emerald-600 text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {i + 1}
                </button>
              ))}
            </div>

            <button
              onClick={() =>
                setCurrentPage(Math.min(totalPages - 1, currentPage + 1))
              }
              disabled={currentPage === totalPages - 1}
              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Next page"
            >
              <svg
                className="w-4 h-4 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Summary when collapsed */}
      {!isExpanded && totalPages > 1 && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="text-sm text-gray-500 text-center">
            Showing {currentEntries.length} of {totalMappings} categories •
            Click expand to see all items
          </div>
        </div>
      )}
    </div>
  );
};

const renderSuccessRateCell = (tracker) => {
  if (
    tracker.task_type === "auto_create" ||
    tracker.task_type === "standardize"
  ) {
    return null;
  }

  const progress =
    tracker.completed_progress_increments /
    Math.max(tracker.total_progress_increments, 1);
  const totalFiles = tracker.total_progress_increments;
  const errorsCount = tracker?.error_data
    ? Object.keys(tracker?.error_data).length
    : 0;
  const successRate = 100 - (errorsCount / (totalFiles * progress)) * 100;
  const processedFiles = Math.ceil(totalFiles * progress);
  const successfulFiles = Math.ceil(processedFiles - errorsCount);
  return (
    <div className="p-3 bg-gray-50 rounded-lg">
      <div className="flex items-center gap-2">
        <FiCheckCircle className="text-green-600" size={12} />
        <span className="text-sm font-medium text-gray-500">Success Rate</span>
      </div>
      <p className="mt-1 text-gray-700 font-medium">
        {`${successRate.toFixed(2)}% (${successfulFiles.toLocaleString()}/${processedFiles.toLocaleString()})`}
      </p>
    </div>
  );
};

const renderSchemaNameCell = (tracker) => {
  if (!tracker?.schema_name) return null;
  return (
    <div className="p-3 bg-gray-50 rounded-lg">
      <div className="flex items-center gap-2">
        <PiGraphFill className="text-primary" size={16} />
        <span className="text-sm font-medium text-gray-500">Schema Name</span>
      </div>
      <p className="mt-1 text-gray-700 font-medium">{tracker.schema_name}</p>
    </div>
  );
};

const renderErrorRateCell = (tracker) => {
  if (!tracker?.overwrite) return null;

  return (
    <div className="p-3 bg-gray-50 rounded-lg">
      <div className="flex items-center gap-2">
        <FiRefreshCw className="text-primary" size={12} />
        <span className="text-sm font-medium text-gray-500">Re-Run</span>
      </div>
      <p className="mt-1 text-gray-700 font-medium">Enabled</p>
    </div>
  );
};

const HierarchyCell = ({ tracker, hierarchy, title = "Tag Schema" }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (!hierarchy) return null;

  return (
    <div className="p-4 bg-white rounded-lg shadow-md border border-gray-100 last:odd:col-span-2 h-full flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <PiGraphFill className="text-emerald-600" size={20} />
          <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center py-6 px-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-primary/30 transition-colors">
        <div className="mb-3 text-center">
          <div className="w-16 h-16 mx-auto mb-2 flex items-center justify-center bg-primary/10 rounded-full">
            <PiGraphFill className="text-primary" size={28} />
          </div>
          <p className="text-sm text-gray-600 mb-1">
            View the schema you ran classification with
          </p>
        </div>

        <button
          onClick={() => setIsModalOpen(true)}
          className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90 shadow-sm hover:shadow transition-all flex items-center gap-2"
          aria-label="Preview schema visualization"
        >
          Preview Schema
        </button>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg w-[90vw] max-w-6xl h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-xl font-semibold text-gray-800">{title}</h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-1 rounded-full hover:bg-gray-100"
                aria-label="Close schema preview"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-gray-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="flex-1 p-4">
              <HierarchyGraph hierarchy={hierarchy} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const HierarchyGraph = ({ hierarchy }) => {
  if (!hierarchy) return null;

  // Default to vertical layout for progress tracking view
  const { nodes, edges } = processData(hierarchy, null, true, "vertical");

  return (
    <ReactFlowProvider>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        minZoom={0.1}
        maxZoom={4}
        defaultEdgeOptions={{
          type: "step",
          style: {
            stroke: "#d1d5db",
          },
        }}
      >
        <Background />
      </ReactFlow>
    </ReactFlowProvider>
  );
};

const renderTimelineCell = (tracker) => {
  return (
    <div className="p-4 bg-white rounded-lg shadow-md">
      <p className="text-xs font-bold uppercase tracking-wider text-gray-800 mb-2">
        Timeline
      </p>
      <div className="flex justify-between items-center gap-8">
        <div>
          <p className="text-xs text-gray-500 mb-1">Started</p>
          <p className="text-sm">{formatDateTime(tracker.start_time)}</p>
        </div>
        <div className="flex-1">
          <div className="h-0.5 bg-gray-200 relative mb-4">
            <div
              className={`absolute inset-y-0 left-0 ${
                tracker.status === "completed"
                  ? "bg-green-400"
                  : tracker.status === "failed" || tracker.status === "aborted"
                    ? "bg-red-400"
                    : "bg-emerald-400"
              }`}
              style={{
                width: `${
                  (tracker.completed_progress_increments /
                    tracker.total_progress_increments) *
                  100
                }%`,
              }}
            />
          </div>

          <div className="w-full flex flex-col items-center justify-center">
            {renderTimelineInfo(tracker)}
          </div>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-1">
            {tracker.status === "completed"
              ? "Completed"
              : tracker.status === "failed"
                ? "Failed"
                : tracker.status === "aborted"
                  ? "Aborted"
                  : "In Progress"}
          </p>
          <p className="text-sm">
            {tracker.end_time
              ? formatDateTime(tracker.end_time)
              : "----------------"}
          </p>
        </div>
      </div>
    </div>
  );
};

const TagsCell = ({ tracker }) => {
  const [isTagSelectionModalOpen, setIsTagSelectionModalOpen] = useState(false);
  const {
    handleSaveGraph,
    selectedDiscoveryGraph,
    graphs,
    setSelectedDiscoveryGraph,
  } = useContext(GraphContext);

  const { deasyApiKey, setSavedTags } = useContext(BaseContext);

  const onConfirmTagSelection = async (filteredTags, selectedTagNames) => {
    // Try to find graph by schema name
    let targetGraph = selectedDiscoveryGraph;

    if (tracker.schema_name) {
      const matchingGraph = graphs.find(
        (g) => g.graph_name === tracker.schema_name,
      );
      if (matchingGraph) {
        targetGraph = matchingGraph;
        // Switch to this graph to make it the active one
        setSelectedDiscoveryGraph(matchingGraph);
      } else {
        // No matching graph, use toast to notify
        toast.error(
          `Schema "${tracker?.schema_name}" not found. Adding to current graph.`,
        );
        targetGraph = selectedDiscoveryGraph;
      }
    }

    const graphTagType = tracker.advanced_settings?.graph_tag_type;
    const structureType =
      tracker.advanced_settings?.max_height === 1 ? "flat" : "hierarchy";

    // Start processing from root
    const updatedData = updateGraphData(
      filteredTags,
      [],
      targetGraph?.graph_data,
      graphTagType,
      structureType,
    );

    // Update graph data and save the correct graph
    await handleSaveGraph({
      ...targetGraph,
      graph_data: updatedData,
    });

    setSelectedDiscoveryGraph(updatedData);

    // attempt to save the tags
    let updatedTags = [];
    for (const tagName of selectedTagNames) {
      try {
        const tagData = tracker.suggested_tags[tagName];

        // Prepare the tag with filtered available_values
        const availableValues = Array.isArray(tagData?.available_values)
          ? tagData.available_values.filter((value) => value !== "<ANY>")
          : [];

        // Create tag object with default empty arrays for examples and available_values
        const tag = {
          ...tagData,
          available_values: availableValues,
          tag_name: tagName,
          examples: Array.isArray(tagData?.examples) ? tagData.examples : [],
        };

        // For date type tags, ensure date_format exists
        if (tag.output_type === "date" && !tag.date_format) {
          tag.date_format = DATE_FORMAT_OPTIONS[0].value;
        }

        updatedTags.push(tag);
        await tagService.upsertTag(tag, deasyApiKey);
      } catch (error) {
        console.error(`Error upserting tag ${tagName}:`, error);
        toast.error(
          `Error upserting tag ${tagName}: ${error.message || error}`,
        );
      }
    }

    const updatedSavedTags = await tagService.getSavedTags(deasyApiKey);
    setSavedTags(updatedSavedTags.data.tags || []);

    // Close the modal
    setIsTagSelectionModalOpen(false);

    // toast success message
    toast.success(`Tags added to ${targetGraph?.graph_name} schema`);
  };

  if (tracker.task_type === "auto_create") {
    return (
      <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow text-left">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FiTag className="text-emerald-600" size={16} />
            <h3 className="text-lg font-semibold text-gray-800">Tag Details</h3>
          </div>
          {tracker.suggested_tags && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsTagSelectionModalOpen(true);
              }}
              className="px-3 py-1.5 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90 flex items-center gap-1 transition-colors"
            >
              <FiPlus size={14} />
              Add to Graph Selector
            </button>
          )}
        </div>
        <div className="h-96 overflow-y-auto pr-2 space-y-4">
          {tracker.suggested_tags &&
            Object.entries(tracker.suggested_tags)
              .filter(([tagName, tag]) => tag.description)
              .map(([tagName, tag]) => (
                <div
                  key={tagName}
                  className="p-4 bg-gray-50 rounded-lg border border-gray-100 hover:border-purple-200 transition-colors"
                >
                  <div className="flex items-start mb-3">
                    <FiHash
                      className="mt-1 text-gray-400 mr-2 flex-shrink-0"
                      size={14}
                    />
                    <div className="flex-1">
                      <h3 className="text-base font-medium text-gray-800 text-left">
                        {tagName}
                      </h3>
                      {tag.description && (
                        <p className="text-sm text-gray-600 mt-1 text-left">
                          {tag.description}
                        </p>
                      )}
                    </div>
                  </div>
                  {tag?.available_values &&
                    Array.isArray(tag?.available_values) &&
                    tag?.available_values?.filter((value) => value !== "<ANY>")
                      .length > 0 && (
                      <div className="mt-3">
                        <div className="flex items-center gap-2 mb-2 ml-6">
                          <FiList
                            className="text-gray-400 flex-shrink-0"
                            size={14}
                          />
                          <span className="text-sm font-medium text-gray-700">
                            Available Values
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2 ml-6">
                          {tag.available_values
                            ?.filter((value) => value !== "<ANY>")
                            .map((value, index) => (
                              <div
                                key={index}
                                className="px-2.5 py-1 bg-white text-gray-700 text-sm border border-gray-200 rounded-md truncate max-w-[160px] hover:border-gray-300 transition-colors"
                              >
                                {value}
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                </div>
              ))}
        </div>

        {/* Tag Selection Modal */}
        <TagSelectionModal
          open={isTagSelectionModalOpen}
          onClose={() => setIsTagSelectionModalOpen(false)}
          suggestedTags={tracker.cleaned_hierarchy}
          tagNotFoundRates={tracker?.tag_not_found_rates}
          onConfirm={async (filteredTags, selectedTagNames) => {
            await onConfirmTagSelection(filteredTags, selectedTagNames);
          }}
          isHierarchical={tracker?.is_hierarchical}
        />
      </div>
    );
  } else if (tracker?.tags) {
    return (
      <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow text-left">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FiTag className="text-emerald-600" size={16} />
            <h3 className="text-lg font-semibold text-gray-800">Tag Details</h3>
          </div>
        </div>
        <div className="h-96 overflow-y-auto pr-2 space-y-4">
          {Object.entries(tracker.tags || {}).map(([tagName, tag]) => {
            // Skip if tag is null or undefined
            if (!tag) return null;

            return (
              <div
                key={tagName}
                className="p-4 bg-gray-50 rounded-lg border border-gray-100 hover:border-purple-200 transition-colors"
              >
                <div className="flex items-start mb-3">
                  <FiHash
                    className="mt-1 text-gray-400 mr-2 flex-shrink-0"
                    size={14}
                  />
                  <div className="flex-1">
                    <h3 className="text-base font-medium text-gray-800 text-left">
                      {tagName}
                    </h3>
                    {tag.description && (
                      <p className="text-sm text-gray-600 mt-1 text-left">
                        {tag.description}
                      </p>
                    )}
                  </div>
                </div>

                {Array.isArray(tag.available_values) &&
                  tag.available_values.length > 0 && (
                    <div className="mt-3">
                      <div className="flex items-center gap-2 mb-2 ml-6">
                        <FiList
                          className="text-gray-400 flex-shrink-0"
                          size={14}
                        />
                        <span className="text-sm font-medium text-gray-700">
                          Available Values
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2 ml-6">
                        {tag.available_values.map((value, index) => (
                          <div
                            key={index}
                            className="px-2.5 py-1 bg-white text-gray-700 text-sm border border-gray-200 rounded-md truncate max-w-[160px] hover:border-gray-300 transition-colors"
                          >
                            {value}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
  return null;
};

const renderTagDetailsCell = (tracker) => {
  if (tracker.task_type !== "standardize") {
    return null;
  }
  return (
    <div className="p-3 bg-gray-50 rounded-lg">
      <div className="flex items-center gap-2">
        <FiHash className="text-primary" size={12} />
        <span className="text-sm font-medium text-gray-500">Tag Name</span>
      </div>
      <p className="mt-1 text-gray-700 font-medium">
        <div className="flex items-start mb-3">
          <div className="flex-1">
            <h3 className="text-base font-medium text-gray-800 text-left">
              {tracker.tag_name}
            </h3>
            {tracker?.description && (
              <p className="text-sm text-gray-600 mt-1 text-left">
                {tracker?.description}
              </p>
            )}
          </div>
        </div>
      </p>
    </div>
  );
};

const renderOnlyDedupeCell = (tracker) => {
  if (tracker.task_type !== "standardize") {
    return null;
  }
  return (
    <div className="p-3 bg-gray-50 rounded-lg">
      <div className="flex items-center gap-2">
        <HiDuplicate className="text-primary" size={12} />
        <span className="text-sm font-medium text-gray-500">Only Dedup</span>
      </div>
      <p className="mt-1 text-gray-700 font-medium">
        <div
          className={`flex items-center ${tracker.only_dedupe ? "text-emerald-600" : "text-gray-500"}`}
        >
          <span
            className={`inline-block w-3 h-3 mr-2 rounded-full ${tracker.only_dedupe ? "bg-emerald-500" : "bg-gray-300"}`}
          ></span>
          <span>{tracker.only_dedupe ? "Enabled" : "Disabled"}</span>
        </div>
      </p>
    </div>
  );
};

const renderProcessingTimeCell = (tracker) => {
  if (!tracker?.end_time) return null;
  return (
    <div className="p-3 bg-gray-50 rounded-lg">
      <div className="flex items-center gap-2">
        <FiClock className="text-primary" size={12} />
        <span className="text-sm font-medium text-gray-500">
          Processing Time
        </span>
      </div>
      <p className="mt-1 text-gray-700 font-medium">
        {formatElapsedTime(
          new Date(tracker.end_time) - new Date(tracker.start_time),
        )}
      </p>
    </div>
  );
};

const AdvancedSettingsCell = ({ tracker, isOpen, setIsOpen }) => {
  if (
    (tracker.task_type !== "auto_create" &&
      tracker.task_type !== "standardize") ||
    !tracker.advanced_settings
  )
    return null;

  return (
    <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow text-left">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FiSettings className="text-emerald-600" size={14} />
          <h3 className="text-lg font-semibold text-gray-800 whitespace-nowrap">
            Advanced Settings
          </h3>
        </div>
        <summary
          onClick={() => setIsOpen(!isOpen)}
          className="cursor-pointer text-sm text-gray-500 hover:text-gray-700 transition-colors flex items-center justify-end"
        >
          {isOpen ? (
            <FiChevronUp className="summary-icon" size={16} />
          ) : (
            <FiChevronDown className="summary-icon" size={16} />
          )}
          {isOpen ? "Hide Details" : "View Details"}
        </summary>
      </div>

      {isOpen && (
        <div className="mt-2">
          <div className="grid grid-cols-2 gap-4">
            {Object.entries(tracker.advanced_settings).map(([key, value]) => {
              if (key === "context" && value === null) {
                return null;
              }
              return (
                <div
                  key={key}
                  className="p-3 bg-gray-50 rounded-lg border border-gray-100 flex flex-col"
                >
                  <div className="flex items-center gap-2 mb-2">
                    {key.includes("max") || key.includes("min") ? (
                      <FiMaximize2 className="text-gray-400" size={14} />
                    ) : key.includes("context") ? (
                      <FiLayers className="text-gray-400" size={14} />
                    ) : key.includes("tag") || key.includes("value") ? (
                      <FiTag className="text-gray-400" size={14} />
                    ) : key.includes("use") ? (
                      <FiToggleRight className="text-gray-400" size={14} />
                    ) : key.includes("graph") ? (
                      <FiTarget className="text-gray-400" size={14} />
                    ) : (
                      <FiSliders className="text-gray-400" size={14} />
                    )}
                    <span className="text-sm font-medium text-gray-700">
                      {key
                        .split("_")
                        .map(
                          (word) =>
                            word.charAt(0).toUpperCase() + word.slice(1),
                        )
                        .join(" ")}
                    </span>
                  </div>
                  <div className="mt-1">
                    {typeof value === "boolean" ? (
                      <div
                        className={`flex items-center ${value ? "text-emerald-600" : "text-gray-500"}`}
                      >
                        <span
                          className={`inline-block w-3 h-3 mr-2 rounded-full ${value ? "bg-emerald-500" : "bg-gray-300"}`}
                        ></span>
                        <span>{value ? "Enabled" : "Disabled"}</span>
                      </div>
                    ) : typeof value === "number" ? (
                      <div className="text-gray-700 font-medium">{value}</div>
                    ) : (
                      <div className="text-gray-700 italic">
                        {key === "ideal_values_per_tag" &&
                        (value === "" || value === null || value === undefined)
                          ? "Auto"
                          : key === "user_context" &&
                              (value === "" ||
                                value === null ||
                                value === undefined)
                            ? "no user context passed"
                            : value}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const renderErrorDataTableCell = (tracker) => {
  if (!tracker?.error_data || Object.keys(tracker.error_data).length === 0) {
    return null;
  }

  return <ErrorDataTable errorData={tracker.error_data} />;
};

export {
  isTrackerStale,
  isTrackerActive,
  renderTimelineInfo,
  renderProgressCell,
  renderFilesCell,
  renderSuccessRateCell,
  renderErrorRateCell,
  HierarchyCell,
  renderTimelineCell,
  TagsCell,
  renderErrorDataTableCell,
  renderProcessingTimeCell,
  renderTagDetailsCell,
  AdvancedSettingsCell,
  ChunkMappingCell,
  renderSchemaNameCell,
  renderOnlyDedupeCell,
};
